/**
 * @zendir/ui - DataValue Component
 *
 * Enterprise-grade data value display with icon, label, value, and status.
 * The building block for telemetry displays, dashboards, and monitoring UIs.
 *
 * AstroUXDS Compliance:
 * - Status colors per AstroUXDS status system
 * - Dual-coding (color + shape) for accessibility via StatusIndicator
 * - Tabular numbers for alignment
 * - Sentence-case labels
 *
 * Features:
 * - Auto-derive status from value + thresholds
 * - Contextual icons from property presets
 * - Multiple layout variants (stacked, inline, compact)
 * - Theme-aware styling
 *
 * @example
 * ```tsx
 * import { DataValue } from '@zendir/ui';
 *
 * // Simple usage with property preset
 * <DataValue property="temperature" value={72.5} />
 *
 * // Custom configuration
 * <DataValue
 *   label="Battery"
 *   value={85}
 *   unit="%"
 *   icon="battery-full"
 *   thresholds={{ critical: 10, caution: 30 }}
 * />
 *
 * // Inline layout
 * <DataValue property="voltage" value={28.4} variant="inline" />
 * ```
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../../theme";
import { AstroIcon, type AstroIconName } from "../display/AstroIcon";
import {
  getPropertyConfig,
  deriveStatus,
  formatPropertyValue,
  type PropertyKey,
  type StatusThresholds,
  type PropertyConfig,
} from "./propertyConfig";
import type { StatusLevel } from "../../utils";

// =============================================================================
// Types
// =============================================================================

export type DataValueVariant =
  | "stacked"
  | "inline"
  | "compact"
  | "minimal"
  | "card"
  | "banner"
  | "stat";
export type DataValueSize = "sm" | "md" | "lg";

export interface DataValueProps {
  /** Property key for auto-configuration (e.g., 'temperature', 'battery') */
  property?: PropertyKey;
  /** Display label (overrides property preset) */
  label?: string;
  /** Current value */
  value: number | string | undefined | null;
  /** Unit of measurement (overrides property preset) */
  unit?: string;
  /** Icon name (overrides property preset) */
  icon?: AstroIconName;
  /** Show icon */
  showIcon?: boolean;
  /** Status thresholds for auto-derivation (overrides property preset) */
  thresholds?: StatusThresholds;
  /** Override status (bypasses auto-derivation) */
  status?: StatusLevel;
  /** Show status indicator */
  showStatus?: boolean;
  /** Precision for number formatting (overrides property preset) */
  precision?: number;
  /** Layout variant */
  variant?: DataValueVariant;
  /** Size */
  size?: DataValueSize;
  /** Custom value color (overrides status color) */
  color?: string;
  /** Additional CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
  /** Tooltip text */
  tooltip?: string;
}

// =============================================================================
// Astro UX Status Border Colors - Required for light theme WCAG compliance
// https://www.astrouxds.com/patterns/status-system/
// =============================================================================

const STATUS_BORDER_COLORS: Record<StatusLevel, string> = {
  off: "#3c3e42",
  standby: "#285766",
  normal: "#005a00",
  caution: "#645600",
  serious: "#664618",
  critical: "#661102",
};

// =============================================================================
// Status Shape SVG - Matches MonitoringIcon exactly
// - Glow effect in dark mode for visibility
// - Border in light mode for WCAG AA compliance
// =============================================================================

interface StatusShapeProps {
  status: StatusLevel;
  size: number;
  color: string;
  borderColor?: string;
  glow?: boolean;
}

const StatusShape = ({
  status,
  size,
  color,
  borderColor,
  glow = true,
}: StatusShapeProps) => {
  const hasBorder = !!borderColor;
  const strokeWidth = hasBorder ? 1 : 0;
  const glowFilter =
    glow && !hasBorder ? `drop-shadow(0 0 3px ${color})` : undefined;

  const renderShape = () => {
    switch (status) {
      case "off":
        return (
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 2.5 : 3}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "standby":
        return (
          <circle
            cx="6"
            cy="6"
            r="3.5"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
        );
      case "normal":
        return (
          <circle
            cx="6"
            cy="6"
            r={hasBorder ? 4.5 : 5}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "caution":
        return (
          <rect
            x={hasBorder ? 1.5 : 1}
            y={hasBorder ? 1.5 : 1}
            width={hasBorder ? 9 : 10}
            height={hasBorder ? 9 : 10}
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "serious":
        return (
          <polygon
            points="6,1 11,6 6,11 1,6"
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      case "critical":
        return (
          <polygon
            points="6,11 1,2 11,2"
            fill={color}
            stroke={borderColor}
            strokeWidth={strokeWidth}
          />
        );
      default:
        return null;
    }
  };

  return (
    <svg
      viewBox="0 0 12 12"
      width={size}
      height={size}
      style={{ filter: glowFilter }}
    >
      {renderShape()}
    </svg>
  );
};

// =============================================================================
// Icon with Status Badge - Enterprise-grade alignment
//
// Design System Principles (Apple HIG / Google Material / AstroUXDS):
// - 4px-based spacing grid for visual harmony
// - Explicit container sizing prevents layout shift
// - Status badge positioned at corner, not extending beyond container bounds
// - Proportional badge sizing: ~35% of icon size for optimal visibility
// =============================================================================

interface IconWithBadgeProps {
  icon: AstroIconName;
  iconSize: number;
  iconColor: string;
  showBadge: boolean;
  status: StatusLevel;
  statusColor: string;
  isLightTheme?: boolean;
}

const IconWithBadge = memo(function IconWithBadge({
  icon,
  iconSize,
  iconColor,
  showBadge,
  status,
  statusColor,
  isLightTheme = false,
}: IconWithBadgeProps) {
  // ═══════════════════════════════════════════════════════════════════════════
  // STATUS BADGE CONTROLS
  // ═══════════════════════════════════════════════════════════════════════════
  // BADGE SIZE: Percentage of icon size (0.25 = 25%)
  const badgeSizeRatio = 0.3;
  const badgeSize = Math.max(
    7,
    Math.min(14, Math.round(iconSize * badgeSizeRatio)),
  );

  // BADGE POSITION: Offset from top-left corner (negative = away from icon)
  const badgeOffsetX = -2; // px (negative = left, positive = right)
  const badgeOffsetY = -2; // px (negative = up, positive = down)

  // ═══════════════════════════════════════════════════════════════════════════
  const borderColor = isLightTheme ? STATUS_BORDER_COLORS[status] : undefined;
  const containerSize = iconSize;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: containerSize,
        height: containerSize,
      }}
    >
      <AstroIcon name={icon} size={iconSize} color={iconColor} />
      {showBadge && (
        <span
          style={{
            position: "absolute",
            top: badgeOffsetY,
            left: badgeOffsetX,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <StatusShape
            status={status}
            size={badgeSize}
            color={statusColor}
            borderColor={borderColor}
            glow={!isLightTheme}
          />
        </span>
      )}
    </div>
  );
});

// =============================================================================
// DataValue Component
// =============================================================================

export const DataValue = memo(function DataValue({
  property,
  label: labelProp,
  value,
  unit: unitProp,
  icon: iconProp,
  showIcon = true,
  thresholds: thresholdsProp,
  status: statusProp,
  showStatus = true,
  precision: precisionProp,
  variant = "stacked",
  size = "md",
  color: colorProp,
  className = "",
  style,
  onClick,
  tooltip,
}: DataValueProps): React.ReactElement {
  const { tokens, mode } = useTheme();
  const isLightTheme = mode === "light";

  // Merge property preset with explicit props
  const config = useMemo((): PropertyConfig => {
    const preset = property
      ? getPropertyConfig(property)
      : {
          label: labelProp || "Value",
          icon: iconProp || "info",
          unit: unitProp,
          precision: precisionProp,
          thresholds: thresholdsProp,
        };

    return {
      ...preset,
      ...(labelProp !== undefined && { label: labelProp }),
      ...(iconProp !== undefined && { icon: iconProp }),
      ...(unitProp !== undefined && { unit: unitProp }),
      ...(precisionProp !== undefined && { precision: precisionProp }),
      ...(thresholdsProp !== undefined && { thresholds: thresholdsProp }),
    };
  }, [property, labelProp, iconProp, unitProp, precisionProp, thresholdsProp]);

  // Derive or use explicit status
  const status = useMemo((): StatusLevel => {
    if (statusProp) return statusProp;
    if (typeof value === "number") {
      return deriveStatus(value, config.thresholds);
    }
    return "normal";
  }, [statusProp, value, config.thresholds]);

  // Format the value
  const formattedValue = useMemo((): string => {
    if (value === undefined || value === null) return "--";
    if (typeof value === "string") return value;
    return formatPropertyValue(value, config);
  }, [value, config]);

  // Get status color
  const statusColor = tokens.colors.status[status] || tokens.colors.status.off;
  const valueColor =
    colorProp ||
    (showStatus && status !== "normal"
      ? statusColor
      : tokens.colors.text.primary);

  // ==========================================================================
  // Size Configuration - Uses theme tokens for consistency with other cards
  // ==========================================================================
  // Typography uses theme tokens to match SpacecraftCard's DataCell
  // fontSize.xs = 10px (0.625rem), fontSize.sm = 12px (0.75rem)
  const sizeConfig = {
    sm: {
      iconSize: 16,
      labelSize: tokens.typography.fontSize.xs, // 10px - matches DataCell
      valueSize: tokens.typography.fontSize.sm, // 12px - matches DataCell
      lineHeight: 1.4,
      gap: 6, // Increased from 4px to 6px for better icon separation with larger icons
      rowGap: 2, // 2px between label and value
    },
    md: {
      iconSize: 20,
      labelSize: tokens.typography.fontSize.xs, // 10px
      valueSize: tokens.typography.fontSize.base, // 14px
      lineHeight: 1.4,
      gap: 10, // Increased from 8px to 10px
      rowGap: 2, // 2px between label and value
    },
    lg: {
      iconSize: 24,
      labelSize: tokens.typography.fontSize.sm, // 12px
      valueSize: tokens.typography.fontSize.md, // 14px
      lineHeight: 1.4,
      gap: 14, // Increased from 12px to 14px
      rowGap: 4, // 4px between label and value
    },
  }[size];

  // ==========================================================================
  // Render Based on Variant
  //
  // Layout principles:
  // - Baseline alignment for inline text
  // - Consistent icon-to-text spacing
  // - Symmetrical padding
  // - Tabular numbers for value alignment in grids
  // ==========================================================================
  const renderContent = () => {
    // Determine if status indicator should show
    const hasStatus = showStatus && status !== "normal" && status !== "off";

    switch (variant) {
      // ======================================================================
      // INLINE: Icon + Label ... Value (space-between)
      // Best for: Key-value pairs in tables or lists
      // ======================================================================
      case "inline": {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: sizeConfig.gap * 2,
              minHeight: sizeConfig.iconSize, // Ensure consistent row height
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: sizeConfig.gap,
              }}
            >
              {showIcon && config.icon && (
                <IconWithBadge
                  icon={config.icon}
                  iconSize={sizeConfig.iconSize}
                  iconColor={
                    hasStatus ? statusColor : tokens.colors.text.tertiary
                  }
                  showBadge={hasStatus}
                  status={status}
                  statusColor={statusColor}
                  isLightTheme={isLightTheme}
                />
              )}
              <span
                style={{
                  fontSize: sizeConfig.labelSize,
                  lineHeight: sizeConfig.lineHeight,
                  color: tokens.colors.text.secondary,
                  fontWeight: 500,
                  fontFamily: tokens.typography.fontFamily.primary,
                }}
              >
                {config.label}
              </span>
            </div>
            <span
              style={{
                fontSize: sizeConfig.valueSize,
                lineHeight: sizeConfig.lineHeight,
                fontWeight: 500, // Match SpacecraftCard DataCell
                fontFamily: tokens.typography.fontFamily.mono,
                fontVariantNumeric: "tabular-nums",
                color: valueColor,
              }}
            >
              {formattedValue}
            </span>
          </div>
        );
      }

      // ======================================================================
      // COMPACT: Icon + Value (horizontal, no label)
      // Best for: Dense displays, status bars, compact dashboards
      // ======================================================================
      case "compact": {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: sizeConfig.gap,
              minHeight: sizeConfig.iconSize,
            }}
          >
            {showIcon && config.icon && (
              <IconWithBadge
                icon={config.icon}
                iconSize={sizeConfig.iconSize}
                iconColor={
                  hasStatus ? statusColor : tokens.colors.text.tertiary
                }
                showBadge={hasStatus}
                status={status}
                statusColor={statusColor}
                isLightTheme={isLightTheme}
              />
            )}
            <span
              style={{
                fontSize: sizeConfig.valueSize,
                lineHeight: sizeConfig.lineHeight,
                fontWeight: 500, // Match SpacecraftCard DataCell
                fontFamily: tokens.typography.fontFamily.mono,
                fontVariantNumeric: "tabular-nums",
                color: valueColor,
              }}
            >
              {formattedValue}
            </span>
          </div>
        );
      }

      // ======================================================================
      // MINIMAL: Value only (no icon, no label)
      // Best for: Inline values, table cells
      // ======================================================================
      case "minimal":
        return (
          <span
            style={{
              fontSize: sizeConfig.valueSize,
              lineHeight: sizeConfig.lineHeight,
              fontWeight: 500, // Match SpacecraftCard DataCell
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
              color: valueColor,
            }}
          >
            {formattedValue}
          </span>
        );

      // ======================================================================
      // CARD: Icon EXACTLY matches 2-row text height
      // Best for: Dashboard tiles, prominent metrics
      // ======================================================================
      case "card": {
        // ═══════════════════════════════════════════════════════════════════════════
        // 2-ROW LAYOUT CONTROLS (card variant)
        // ═══════════════════════════════════════════════════════════════════════════
        // ICON MULTIPLIER: Scale factor (0.5 = half, 1.0 = match text height, 1.5 = 150%)
        const iconMultiplier = 0.7;

        // GAP: Pixels between icon and text column
        const iconToTextGap = 4; // px

        // ═══════════════════════════════════════════════════════════════════════════
        const baseSize = { sm: 26, md: 32, lg: 40 }[size];
        const iconSize = Math.round(baseSize * iconMultiplier);

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: iconToTextGap,
            }}
          >
            {showIcon && config.icon && (
              <IconWithBadge
                icon={config.icon}
                iconSize={iconSize}
                iconColor={
                  hasStatus ? statusColor : tokens.colors.text.tertiary
                }
                showBadge={hasStatus}
                status={status}
                statusColor={statusColor}
                isLightTheme={isLightTheme}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // Distribute label/value to edges
                gap: sizeConfig.rowGap,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: sizeConfig.labelSize,
                  lineHeight: sizeConfig.lineHeight,
                  color: tokens.colors.text.secondary,
                  fontWeight: 500,
                  fontFamily: tokens.typography.fontFamily.primary,
                }}
              >
                {config.label}
              </span>
              <span
                style={{
                  fontSize: sizeConfig.valueSize,
                  lineHeight: sizeConfig.lineHeight,
                  fontWeight: 500, // Match SpacecraftCard DataCell
                  fontFamily: tokens.typography.fontFamily.mono,
                  fontVariantNumeric: "tabular-nums",
                  color: valueColor,
                }}
              >
                {formattedValue}
              </span>
            </div>
          </div>
        );
      }

      // ======================================================================
      // BANNER: Label on top, icon left-aligned + value right-aligned below
      // Best for: TopBar metrics, KPI tiles where heading spans full width
      // ======================================================================
      case "banner": {
        const bannerIconSize = { sm: 18, md: 22, lg: 28 }[size];
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: sizeConfig.rowGap,
              minWidth: 0,
              width: "100%",
            }}
          >
            <span
              style={{
                fontSize: sizeConfig.labelSize,
                lineHeight: sizeConfig.lineHeight,
                color: tokens.colors.text.secondary,
                fontWeight: 500,
                fontFamily: tokens.typography.fontFamily.primary,
                textTransform: "uppercase" as const,
                letterSpacing: "0.5px",
              }}
            >
              {config.label}
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                gap: 6,
                minWidth: 0,
              }}
            >
              {showIcon && config.icon && (
                <IconWithBadge
                  icon={config.icon}
                  iconSize={bannerIconSize}
                  iconColor={
                    hasStatus ? statusColor : tokens.colors.text.tertiary
                  }
                  showBadge={hasStatus}
                  status={status}
                  statusColor={statusColor}
                  isLightTheme={isLightTheme}
                />
              )}
              <span
                style={{
                  fontSize: sizeConfig.valueSize,
                  lineHeight: sizeConfig.lineHeight,
                  fontWeight: 600,
                  fontFamily: tokens.typography.fontFamily.mono,
                  fontVariantNumeric: "tabular-nums",
                  color: valueColor,
                  textAlign: "left",
                }}
              >
                {formattedValue}
              </span>
            </div>
          </div>
        );
      }

      // ======================================================================
      // STAT: icon + value (status-coloured) on top, uppercase mono label below
      // Best for: status-header / top-bar telemetry chips (Asset, Link,
      // Battery, Storage, Ground). Unlike the other variants, the value AND
      // icon take the status colour even when `normal` — a nominal chip
      // renders green — matching the SpaceRange status-header spec.
      // ======================================================================
      case "stat": {
        const statSize = {
          sm: { icon: 13, value: 13, label: 9.5 },
          md: { icon: 15, value: 14, label: 10 },
          lg: { icon: 18, value: 16, label: 11 },
        }[size];
        // colorProp wins; otherwise the status colour drives value + icon
        // (including `normal`, so nominal telemetry reads green per spec).
        const statColor = colorProp || statusColor;
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontFamily: tokens.typography.fontFamily.mono,
                fontSize: statSize.value,
                fontWeight: 600,
                color: statColor,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "0.02em",
                lineHeight: 1.1,
              }}
            >
              {showIcon && config.icon && (
                <AstroIcon
                  name={config.icon}
                  size={statSize.icon}
                  color={statColor}
                />
              )}
              <span>{formattedValue}</span>
            </div>
            <span
              style={{
                fontFamily: tokens.typography.fontFamily.mono,
                fontSize: statSize.label,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: tokens.colors.text.tertiary,
                lineHeight: 1.1,
              }}
            >
              {config.label}
            </span>
          </div>
        );
      }

      // ======================================================================
      // STACKED (default): Icon EXACTLY matches 2-row text height
      // Best for: General telemetry displays, standard data values
      // ======================================================================
      case "stacked":
      default: {
        // ═══════════════════════════════════════════════════════════════════════════
        // 2-ROW LAYOUT CONTROLS (stacked variant)
        // ═══════════════════════════════════════════════════════════════════════════
        // ICON MULTIPLIER: Scale factor (0.5 = half, 1.0 = match text height, 1.5 = 150%)
        const iconMultiplier = 0.7;

        // GAP: Pixels between icon and text column
        const iconToTextGap = 4; // px

        // ═══════════════════════════════════════════════════════════════════════════
        const baseSize = { sm: 26, md: 32, lg: 40 }[size];
        const iconSize = Math.round(baseSize * iconMultiplier);

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: iconToTextGap,
            }}
          >
            {showIcon && config.icon && (
              <IconWithBadge
                icon={config.icon}
                iconSize={iconSize}
                iconColor={
                  hasStatus ? statusColor : tokens.colors.text.tertiary
                }
                showBadge={hasStatus}
                status={status}
                statusColor={statusColor}
                isLightTheme={isLightTheme}
              />
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between", // Distribute label/value to edges
                gap: sizeConfig.rowGap,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: sizeConfig.labelSize,
                  lineHeight: sizeConfig.lineHeight,
                  color: tokens.colors.text.secondary,
                  fontWeight: 500,
                  fontFamily: tokens.typography.fontFamily.primary,
                }}
              >
                {config.label}
              </span>
              <span
                style={{
                  fontSize: sizeConfig.valueSize,
                  lineHeight: sizeConfig.lineHeight,
                  fontWeight: 500, // Match SpacecraftCard DataCell
                  fontFamily: tokens.typography.fontFamily.mono,
                  fontVariantNumeric: "tabular-nums",
                  color: valueColor,
                }}
              >
                {formattedValue}
              </span>
            </div>
          </div>
        );
      }
    }
  };

  return (
    <div
      className={`zendir-data-value zendir-data-value--${variant} zendir-data-value--${size} ${className}`}
      style={{
        cursor: onClick ? "pointer" : "default",
        ...(variant === "banner" ? { flex: 1, minWidth: 0 } : {}),
        ...style,
      }}
      onClick={onClick}
      title={tooltip || config.description}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      {renderContent()}
    </div>
  );
});

// =============================================================================
// DataValueGroup Component
// =============================================================================

export interface DataValueGroupProps {
  /** Title for the group */
  title?: string;
  /** Icon for the title */
  icon?: AstroIconName;
  /** Layout direction */
  direction?: "row" | "column" | "grid";
  /** Number of columns for grid layout */
  columns?: number;
  /** Gap between items */
  gap?: "sm" | "md" | "lg";
  /** Children DataValue components */
  children: React.ReactNode;
  /** Additional CSS class */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

export const DataValueGroup = memo(function DataValueGroup({
  title,
  icon,
  direction = "grid",
  columns = 2,
  gap = "md",
  children,
  className = "",
  style,
}: DataValueGroupProps): React.ReactElement {
  const { tokens } = useTheme();

  // Gap sizing on 4px grid
  const gapSize = {
    sm: 8, // 2 × 4px
    md: 16, // 4 × 4px
    lg: 24, // 6 × 4px
  }[gap];

  const layoutStyles: Record<typeof direction, React.CSSProperties> = {
    row: {
      display: "flex",
      flexDirection: "row" as const,
      flexWrap: "wrap" as const,
      gap: gapSize,
      alignItems: "flex-start",
    },
    column: {
      display: "flex",
      flexDirection: "column" as const,
      gap: gapSize,
    },
    grid: {
      display: "grid",
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: gapSize,
      alignItems: "start",
    },
  };
  const layoutStyle = layoutStyles[direction];

  return (
    <div className={`zendir-data-value-group ${className}`} style={style}>
      {title && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6, // 6px gap for tight header
            marginBottom: 12, // 3 × 4px
            paddingBottom: 8, // 2 × 4px
            borderBottom: `1px solid ${tokens.colors.border.muted}`,
          }}
        >
          {icon && (
            <AstroIcon
              name={icon}
              size={14}
              color={tokens.colors.text.tertiary}
              style={{ flexShrink: 0 }}
            />
          )}
          <span
            style={{
              fontSize: "12px",
              fontWeight: 500,
              color: tokens.colors.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </span>
        </div>
      )}
      <div style={layoutStyle}>{children}</div>
    </div>
  );
});

export default DataValue;
